import { Bot, Clock, Eye, MapPin, Mic, Monitor } from "lucide-react";
import React from "react";

import {
  type EvidenceEvent,
  type EvidenceRecord,
  type IpLocationMismatchSignalEvidence,
  type LlmAnswerSimilaritySignalEvidence,
  type LlmFlaggedQuestion,
  type ScriptedCitation,
  type ScriptedResponsesSignalEvidence,
  type SignalEvidence,
  type SignalType,
  type TabSwitchingSignalEvidence,
  type WindowBlurSignalEvidence,
} from "./types";

export const SIGNAL_CONFIG: Record<
  string,
  { label: string; icon: React.ElementType }
> = {
  IP_LOCATION_MISMATCH: { label: "IP Location Mismatch", icon: MapPin },
  TAB_SWITCHING: { label: "Tab Switching Detected", icon: Monitor },
  WINDOW_BLUR: { label: "Window Out of Focus", icon: Eye },
  LLM_ANSWER_SIMILARITY: { label: "LLM Answer Match", icon: Bot },
  SCRIPTED_RESPONSES: { label: "Responses Sound Scripted", icon: Mic },
  ANSWER_TIMING: { label: "Answer Timing", icon: Clock },
};

export const SEVERITY_CONFIG: Record<
  string,
  { label: string; colorScheme: string; iconBg: string; iconColor: string }
> = {
  HIGH: {
    label: "High Risk",
    colorScheme: "red",
    iconBg: "red.100",
    iconColor: "red.600",
  },
  MEDIUM: {
    label: "Medium Risk",
    colorScheme: "orange",
    iconBg: "orange.100",
    iconColor: "orange.700",
  },
  LOW: {
    label: "Low Risk",
    colorScheme: "yellow",
    iconBg: "yellow.100",
    iconColor: "yellow.700",
  },
  NONE: {
    label: "None",
    colorScheme: "green",
    iconBg: "green.100",
    iconColor: "green.600",
  },
};

export const formatDuration = (ms: number): string => {
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}s`;
  if (seconds === 0) return `${minutes}m`;
  return `${minutes}m ${seconds}s`;
};

export const formatTimestamp = (
  isoTimestamp: string,
  interviewStartTime?: string | null
): string => {
  const time = new Date(isoTimestamp);
  if (interviewStartTime) {
    const start = new Date(interviewStartTime);
    const offsetMs = Math.max(0, time.getTime() - start.getTime());
    const totalSeconds = Math.floor(offsetMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0"
    )}`;
  }
  return time.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export const getSeekOffset = (
  isoTimestamp: string,
  interviewStartTime?: string | null
): number | undefined => {
  if (!interviewStartTime) return undefined;
  const offsetMs =
    new Date(isoTimestamp).getTime() - new Date(interviewStartTime).getTime();
  return Math.max(0, offsetMs / 1000);
};

export const parseEvidence = (
  signalType: string,
  evidence?: string | null
): SignalEvidence => {
  if (!evidence) {
    return {
      signalType: "UNKNOWN",
      rawSignalType: signalType,
      evidence: null,
    };
  }
  try {
    const parsed = JSON.parse(evidence);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {
        signalType: "UNKNOWN",
        rawSignalType: signalType,
        evidence: null,
      };
    }
    return normalizeEvidence(signalType, parsed as EvidenceRecord);
  } catch {
    return {
      signalType: "UNKNOWN",
      rawSignalType: signalType,
      evidence: null,
    };
  }
};

const getNumberFromAny = (
  evidence: EvidenceRecord,
  ...keys: string[]
): number | undefined => {
  const matchingKey = keys.find((key) => typeof evidence[key] === "number");
  return matchingKey ? (evidence[matchingKey] as number) : undefined;
};

const getStringFromAny = (
  evidence: EvidenceRecord,
  ...keys: string[]
): string | undefined => {
  const matchingKey = keys.find((key) => typeof evidence[key] === "string");
  return matchingKey ? (evidence[matchingKey] as string) : undefined;
};

const normalizeEvents = (raw: unknown): EvidenceEvent[] | undefined => {
  if (!Array.isArray(raw)) return undefined;
  const events = raw
    .map((event) => {
      if (!event || typeof event !== "object") return null;
      const payload = event as Record<string, unknown>;
      const { start } = payload;
      const { end } = payload;
      const durationMs = payload.duration_ms;
      if (
        typeof start !== "string" ||
        typeof end !== "string" ||
        typeof durationMs !== "number"
      ) {
        return null;
      }
      return { start, end, durationMs };
    })
    .filter((event): event is EvidenceEvent => event !== null);
  return events;
};

const normalizeEvidence = (
  signalType: string,
  evidence: EvidenceRecord
): SignalEvidence => {
  switch (signalType as SignalType) {
    case "TAB_SWITCHING":
      return buildTabSwitchingSignalEvidence(evidence);
    case "WINDOW_BLUR":
      return buildWindowBlurSignalEvidence(evidence);
    case "IP_LOCATION_MISMATCH":
      return buildIpLocationMismatchSignalEvidence(evidence);
    case "LLM_ANSWER_SIMILARITY":
      return buildLlmAnswerSimilaritySignalEvidence(evidence);
    case "SCRIPTED_RESPONSES":
      return buildScriptedResponsesSignalEvidence(evidence);
    default:
      return { signalType: "UNKNOWN", rawSignalType: signalType, evidence };
  }
};

const buildTabSwitchingSignalEvidence = (
  evidence: EvidenceRecord
): TabSwitchingSignalEvidence => {
  return {
    signalType: "TAB_SWITCHING",
    evidence: {
      totalCount: getNumberFromAny(evidence, "total_count"),
      events: normalizeEvents(evidence.events),
    },
  };
};

const buildWindowBlurSignalEvidence = (
  evidence: EvidenceRecord
): WindowBlurSignalEvidence => {
  return {
    signalType: "WINDOW_BLUR",
    evidence: {
      totalDurationMs: getNumberFromAny(evidence, "total_duration_ms"),
      events: normalizeEvents(evidence.events),
    },
  };
};

const buildIpLocationMismatchSignalEvidence = (
  evidence: EvidenceRecord
): IpLocationMismatchSignalEvidence => {
  return {
    signalType: "IP_LOCATION_MISMATCH",
    evidence: {
      reason: getStringFromAny(evidence, "reason"),
      ipLocation: getStringFromAny(evidence, "ip_location"),
      resumeLocation: getStringFromAny(evidence, "resume_location"),
    },
  };
};

const normalizeLlmQuestions = (
  raw: unknown
): LlmFlaggedQuestion[] | undefined => {
  if (!Array.isArray(raw)) return undefined;
  return raw
    .map((q): LlmFlaggedQuestion | null => {
      if (!q || typeof q !== "object") return null;
      const payload = q as Record<string, unknown>;
      return {
        questionId:
          typeof payload.question_id === "string"
            ? payload.question_id
            : undefined,
        questionText:
          typeof payload.question_text === "string"
            ? payload.question_text
            : undefined,
        questionNumber:
          typeof payload.question_number === "number"
            ? payload.question_number
            : undefined,
        likelyAiGenerated:
          typeof payload.likely_ai_generated === "boolean"
            ? payload.likely_ai_generated
            : undefined,
        confidence:
          typeof payload.confidence === "number"
            ? payload.confidence
            : undefined,
        citationSeconds:
          typeof payload.citation_seconds === "number"
            ? payload.citation_seconds
            : null,
        topicLabel:
          typeof payload.topic_label === "string"
            ? payload.topic_label
            : undefined,
      };
    })
    .filter((q): q is LlmFlaggedQuestion => q !== null);
};

const buildLlmAnswerSimilaritySignalEvidence = (
  evidence: EvidenceRecord
): LlmAnswerSimilaritySignalEvidence => {
  return {
    signalType: "LLM_ANSWER_SIMILARITY",
    evidence: {
      flaggedAnswerCount: getNumberFromAny(evidence, "flagged_count"),
      totalAnswerCount: getNumberFromAny(evidence, "total_questions"),
      questions: normalizeLlmQuestions(evidence.questions),
    },
  };
};

const normalizeScriptedCitations = (
  raw: unknown
): ScriptedCitation[] | undefined => {
  if (!Array.isArray(raw)) return undefined;
  return raw
    .map((c): ScriptedCitation | null => {
      if (!c || typeof c !== "object") return null;
      const payload = c as Record<string, unknown>;
      const citationSeconds = payload.citation_seconds;
      if (typeof citationSeconds !== "number") return null;
      return { citationSeconds };
    })
    .filter((c): c is ScriptedCitation => c !== null);
};

const buildScriptedResponsesSignalEvidence = (
  evidence: EvidenceRecord
): ScriptedResponsesSignalEvidence => {
  return {
    signalType: "SCRIPTED_RESPONSES",
    evidence: {
      userVisibleSummary: getStringFromAny(evidence, "user_visible_summary"),
      aggregateReadingConfidence: getNumberFromAny(
        evidence,
        "aggregate_reading_confidence"
      ),
      naturalBaselineCitations: normalizeScriptedCitations(
        evidence.natural_baseline_citations
      ),
      scriptedCitations: normalizeScriptedCitations(
        evidence.scripted_citations
      ),
    },
  };
};

type IntegritySignal = {
  signalType: string;
  severity: string;
  detected: boolean;
  evidence?: string | null;
};

export type IntegrityTimelineEvent = {
  /** Stable id for progress-bar segment list keys. */
  eventKey: string;
  timestamp: number;
  durationSeconds?: number;
  label: string;
  severity: "HIGH" | "MEDIUM" | "LOW";
};

/**
 * Extract timeline events from integrity signals evidence for display
 * on the media player progress bar.
 */
export function extractIntegrityTimelineEvents(
  integritySignals: IntegritySignal[],
  startTime: string | null | undefined
): IntegrityTimelineEvent[] {
  return integritySignals
    .filter((signal) => signal.detected && signal.evidence)
    .flatMap((signal) => {
      const severity = signal.severity as "HIGH" | "MEDIUM" | "LOW";
      const label =
        SIGNAL_CONFIG[signal.signalType]?.label ?? signal.signalType;
      const parsed = parseEvidence(signal.signalType, signal.evidence);

      if (
        parsed.signalType === "TAB_SWITCHING" ||
        parsed.signalType === "WINDOW_BLUR"
      ) {
        const events = parsed.evidence?.events ?? [];
        return events.flatMap((ev) => {
          const offset = getSeekOffset(ev.start, startTime);
          if (offset == null) return [];
          const durationMs = ev.durationMs ?? 0;
          const durationSeconds = durationMs / 1000;
          const durationStr = formatDuration(durationMs);
          const eventLabel =
            durationSeconds > 0 ? `${label} (${durationStr})` : label;
          return [
            {
              eventKey: `${parsed.signalType}-${ev.start}-${ev.end}`,
              timestamp: offset,
              durationSeconds,
              label: eventLabel,
              severity,
            },
          ];
        });
      }

      if (parsed.signalType === "LLM_ANSWER_SIMILARITY") {
        const questions = parsed.evidence?.questions ?? [];
        return questions
          .filter(
            (q) =>
              q.likelyAiGenerated &&
              q.citationSeconds != null &&
              q.citationSeconds >= 0
          )
          .map((q) => {
            const questionLabel = q.topicLabel
              ? `${label}: Q${q.questionNumber ?? ""} ${q.topicLabel}`
              : label;
            return {
              eventKey: `LLM_ANSWER_SIMILARITY-${String(
                q.citationSeconds
              )}-${String(q.questionNumber ?? "")}-${String(
                q.questionId ?? ""
              )}`,
              timestamp: q.citationSeconds as number,
              label: questionLabel,
              severity,
            };
          });
      }

      if (parsed.signalType === "SCRIPTED_RESPONSES") {
        const citations = parsed.evidence?.scriptedCitations ?? [];
        return citations.map((c) => ({
          eventKey: `SCRIPTED_RESPONSES-${String(c.citationSeconds)}`,
          timestamp: c.citationSeconds,
          label,
          severity,
        }));
      }

      return [];
    });
}
